import type { FC } from 'react';
import React, { useCallback, useContext, Fragment } from 'react';
import type { WithIntlProps, WrappedComponentProps } from 'react-intl';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';

import Tooltip from '@atlaskit/tooltip/Tooltip';
import StarIcon from '@atlaskit/icon/core/star-unstarred';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { StarSelectedIcon } from '@confluence/icons/entry-points/StarSelectedIcon.ts';

import { FeedCardContext } from '../../FeedCardContext.ts';
import { StyledActionButton, ActionButtonWrapper } from '../ActionButtonStyles.tsx';

import { useContentStar } from './useContentStar.tsx';

const i18n = defineMessages({
	star: {
		id: 'feed.card.star.general',
		defaultMessage: 'Star',
		description: 'Text for the star button in the ellipses dropdown menu',
	},
	unstar: {
		id: 'feed.card.unstar.general',
		defaultMessage: 'Unstar',
		description: 'Text for the unstar button in the ellipses dropdown menu',
	},
});

type Props = {
	contentId: string;
	isStarred: boolean;
};

const FeedCardStarActionComponent: FC<WrappedComponentProps & Props> = ({
	intl,
	contentId,
	isStarred,
}) => {
	const { fireAnalyticsEvent } = useContext(FeedCardContext);

	const [toggleIsStar, starMutationError] = useContentStar(contentId);

	const handleStarButton = useCallback(async () => {
		await toggleIsStar(isStarred);
		fireAnalyticsEvent({
			type: 'sendUIEvent',
			action: isStarred ? 'deleted' : 'created',
			actionSubject: 'favouritePage',
			actionSubjectId: 'feedCardDropdown',
		});
	}, [fireAnalyticsEvent, toggleIsStar, isStarred]);

	const icon = isStarred ? (
		<StarSelectedIcon label={intl.formatMessage(i18n.unstar)} />
	) : (
		<StarIcon label={intl.formatMessage(i18n.star)} />
	);

	return (
		<Fragment>
			{starMutationError && <ErrorDisplay error={starMutationError} />}
			<Tooltip
				content={<FormattedMessage {...(isStarred ? i18n.unstar : i18n.star)} />}
				position="top"
				hideTooltipOnClick
			>
				<ActionButtonWrapper forceShow={isStarred}>
					<StyledActionButton onClick={handleStarButton} appearance="subtle" iconBefore={icon} />
				</ActionButtonWrapper>
			</Tooltip>
		</Fragment>
	);
};

export const FeedCardStarAction: FC<WithIntlProps<WrappedComponentProps & Props>> & {
	WrappedComponent: React.ComponentType<WrappedComponentProps & Props>;
} = injectIntl(FeedCardStarActionComponent);
